import {
  IonButton,
  IonIcon,
  IonInput,
  IonSpinner,
} from '@ionic/react';
import {
  arrowBackOutline,
  lockClosedOutline,
  logOutOutline,
  personCircleOutline,
  refreshOutline,
} from 'ionicons/icons';
import { useEffect, useRef } from 'react';
import type { SharedTabletOperator } from '../lib/auth/sharedTabletOperatorTypes.js';

interface SharedTabletLockOverlayProps {
  operators: SharedTabletOperator[];
  selectedOperatorId: string | null;
  pin: string;
  pinRequired: boolean;
  busy: boolean;
  loading: boolean;
  offline: boolean;
  error: string | null;
  onSelect: (operatorId: string | null) => void;
  onPinChange: (pin: string) => void;
  onUnlock: () => void;
  onRefresh: () => void;
  onSignOut: () => void;
}

export function SharedTabletLockOverlay(
  props: SharedTabletLockOverlayProps,
): JSX.Element {
  const pinRef = useRef<HTMLIonInputElement>(null);
  const selected = props.operators.find(
    (operator) => operator.tenant_personnel_id === props.selectedOperatorId,
  ) ?? null;

  useEffect(() => {
    if (selected && !props.busy) {
      window.setTimeout(() => { void pinRef.current?.setFocus(); }, 80);
    }
  }, [selected, props.busy]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="shared-tablet-lock-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100_000,
        overflowY: 'auto',
        background: 'linear-gradient(160deg, #e8f2ff 0%, #f8fafc 45%, #e8f7f5 100%)',
        padding: 'calc(env(safe-area-inset-top) + 24px) 18px calc(env(safe-area-inset-bottom) + 24px)',
      }}
    >
      <main style={{ width: 'min(100%, 520px)', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{
            width: 58,
            height: 58,
            borderRadius: 18,
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 12px',
            background: '#0066cc',
            boxShadow: '0 10px 26px rgba(0,102,204,.22)',
          }}>
            <IonIcon icon={lockClosedOutline} aria-hidden="true" style={{ color: '#fff', fontSize: 28 }} />
          </div>
          <h1 id="shared-tablet-lock-title" style={{ margin: 0, color: '#0f172a', fontSize: 25, lineHeight: 1.2 }}>
            {selected ? `Ciao, ${selected.full_name}` : 'Chi sta usando TrackEAT?'}
          </h1>
          <p style={{ margin: '8px auto 0', color: '#475569', fontSize: 15, lineHeight: 1.5, maxWidth: 420 }}>
            {selected
              ? (props.pinRequired
                ? 'Inserisci il tuo PIN personale per firmare le attività a tuo nome.'
                : 'Conferma il tuo nome: le attività saranno firmate a tuo nome.')
              : 'Seleziona il tuo nome. Il login del tablet resta attivo, ma ogni registrazione sarà attribuita alla persona scelta.'}
          </p>
        </div>

        <section style={{
          background: '#fff',
          border: '1px solid #dbe5f0',
          borderRadius: 18,
          padding: 16,
          boxShadow: '0 14px 40px rgba(15,23,42,.10)',
        }}>
          {props.loading && props.operators.length === 0 ? (
            <div role="status" style={{ display: 'grid', placeItems: 'center', minHeight: 160, color: '#64748b', gap: 10 }}>
              <IonSpinner name="crescent" />
              Verifica account e personale…
            </div>
          ) : selected ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                props.onUnlock();
              }}
            >
              <button
                type="button"
                onClick={() => props.onSelect(null)}
                disabled={props.busy}
                style={{
                  minHeight: 44,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  border: 0,
                  background: 'transparent',
                  color: '#0066cc',
                  fontSize: 14,
                  fontWeight: 700,
                  padding: '0 6px',
                  cursor: 'pointer',
                }}
              >
                <IonIcon icon={arrowBackOutline} aria-hidden="true" />
                Cambia persona
              </button>

              {props.pinRequired && <>
                <label htmlFor="shared-tablet-pin" style={{ display: 'block', margin: '14px 2px 7px', color: '#334155', fontSize: 14, fontWeight: 700 }}>
                  PIN personale a 4 cifre
                </label>
                <IonInput
                  ref={pinRef}
                  id="shared-tablet-pin"
                  type="password"
                  inputmode="numeric"
                  autocomplete="off"
                  maxlength={4}
                  value={props.pin}
                  disabled={props.busy}
                  aria-describedby={props.error ? 'shared-tablet-pin-error' : undefined}
                  onIonInput={(event) => props.onPinChange(
                    String(event.detail.value ?? '').replace(/\D/g, '').slice(0, 4),
                  )}
                  style={{
                    '--background': '#f8fafc', '--padding-start': '16px', '--padding-end': '16px',
                    border: `1px solid ${props.error ? '#dc2626' : '#cbd5e1'}`, borderRadius: 12,
                    minHeight: 54, fontSize: 24, fontWeight: 800, letterSpacing: '.28em', textAlign: 'center',
                  } as React.CSSProperties}
                />
              </>}
              {props.error && (
                <p id="shared-tablet-pin-error" role="alert" aria-live="assertive" style={{ margin: '8px 2px 0', color: '#b91c1c', fontSize: 13, lineHeight: 1.4 }}>
                  {props.error}
                </p>
              )}
              <IonButton
                type="submit"
                expand="block"
                disabled={props.busy || (props.pinRequired && props.pin.length !== 4)}
                style={{ marginTop: 16, minHeight: 48, '--border-radius': '12px' } as React.CSSProperties}
              >
                {props.busy ? <IonSpinner name="dots" /> : 'Entra e firma a mio nome'}
              </IonButton>
            </form>
          ) : props.operators.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '22px 8px' }}>
              <IonIcon icon={personCircleOutline} aria-hidden="true" style={{ fontSize: 42, color: '#94a3b8' }} />
              <h2 style={{ margin: '10px 0 6px', fontSize: 17, color: '#0f172a' }}>
                {props.error ? 'Elenco operatori non disponibile' : 'Nessun operatore abilitato'}
              </h2>
              <p role={props.error ? 'alert' : undefined} style={{ margin: 0, color: props.error ? '#b91c1c' : '#64748b', fontSize: 14, lineHeight: 1.5 }}>
                {props.error ?? 'Un responsabile deve abilitare il personale da Impostazioni → Personale.'}
              </p>
              {(!props.offline || props.error) && (
                <IonButton fill="outline" disabled={props.loading} onClick={props.onRefresh} style={{ marginTop: 14 }}>
                  <IonIcon icon={refreshOutline} slot="start" />
                  {props.error ? 'Riprova' : 'Aggiorna elenco'}
                </IonButton>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {props.error && (
                <div>
                  <p role="alert" style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: 13, lineHeight: 1.4 }}>{props.error}</p>
                  <IonButton fill="outline" disabled={props.loading} onClick={props.onRefresh}>
                    <IonIcon icon={refreshOutline} slot="start" />
                    Riprova
                  </IonButton>
                </div>
              )}
              {props.operators.map((operator) => (
                <button
                  key={operator.tenant_personnel_id}
                  type="button"
                  onClick={() => props.onSelect(operator.tenant_personnel_id)}
                  style={{
                    width: '100%',
                    minHeight: 64,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    border: '1px solid #dbe5f0',
                    borderRadius: 13,
                    background: '#fff',
                    color: '#0f172a',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <IonIcon icon={personCircleOutline} aria-hidden="true" style={{ fontSize: 34, color: '#0066cc', flexShrink: 0 }} />
                  <span style={{ minWidth: 0 }}>
                    <strong style={{ display: 'block', fontSize: 16 }}>{operator.full_name}</strong>
                    <span style={{ display: 'block', color: '#64748b', fontSize: 13, marginTop: 2 }}>{operator.role}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        <p aria-live="polite" style={{ textAlign: 'center', color: props.offline ? '#92400e' : '#64748b', fontSize: 12.5, margin: '13px 4px 0' }}>
          {props.offline
            ? 'Modalità offline: possono entrare solo le persone già abilitate su questo tablet.'
            : 'Il tablet si blocca quando lo schermo si chiude o dopo 2 minuti senza attività.'}
        </p>

        <IonButton
          fill="clear"
          color="danger"
          expand="block"
          disabled={props.busy}
          onClick={props.onSignOut}
          aria-label="Esci dall'account tecnico del tablet"
          style={{ marginTop: 10, minHeight: 48 }}
        >
          <IonIcon icon={logOutOutline} slot="start" aria-hidden="true" />
          Esci dall'account del tablet
        </IonButton>
      </main>
    </div>
  );
}
